import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FuturaSmileIcon from './icons/FuturaSmileIcon';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';
import GlobalAnnouncementBanner from './GlobalAnnouncementBanner';

interface HeaderProps {

  appState: 'site' | 'app';
  onLaunchApp: () => void;
  onGoHome: () => void;
}

const Header: React.FC<HeaderProps> = ({ appState, onLaunchApp, onGoHome }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      <GlobalAnnouncementBanner />
      <div className="max-w-[1200px] mx-auto px-5 py-5">
        <nav className="flex items-center justify-between px-6 py-4 border border-[#27272a] rounded-xl bg-[rgba(16,16,18,0.8)] backdrop-blur-[12px]">

          {/* Logo */}
          <button onClick={onGoHome} className="flex items-center group" aria-label="Go to homepage">
            <img
              src="/images/Logo.webp"
              alt="FuturaSmile"
              className="h-7 w-auto object-contain"
            />
          </button>

          {/* Desktop Navigation - Pill Container */}
          {appState === 'site' && (
            <div className="hidden md:flex items-center gap-1 bg-[#18181b] px-1 py-1 rounded-full border border-[#27272a]">
              <button
                onClick={() => handleNavigation('/')}
                className={`px-6 py-2 rounded-[20px] text-[0.9rem] font-medium transition-all duration-300 ${location.pathname === '/'
                  ? 'bg-[#27272a] text-white'
                  : 'text-[#9CA3AF] hover:text-white'
                  }`}
              >
                Home
              </button>
              <button
                onClick={() => handleNavigation('/pricing')}
                className={`px-6 py-2 rounded-[20px] text-[0.9rem] font-medium transition-all duration-300 ${isActive('/pricing')
                  ? 'bg-[#27272a] text-white'
                  : 'text-[#9CA3AF] hover:text-white'
                  }`}
              >
                Pricing
              </button>
              <button
                onClick={() => handleNavigation('/guide')}
                className={`px-6 py-2 rounded-[20px] text-[0.9rem] font-medium transition-all duration-300 ${isActive('/guide')
                  ? 'bg-[#27272a] text-white'
                  : 'text-[#9CA3AF] hover:text-white'
                  }`}
              >
                Guide
              </button>
              <button
                onClick={() => handleNavigation('/about')}
                className={`px-6 py-2 rounded-[20px] text-[0.9rem] font-medium transition-all duration-300 ${isActive('/about')
                  ? 'bg-[#27272a] text-white'
                  : 'text-[#9CA3AF] hover:text-white'
                  }`}
              >
                About
              </button>
              <button
                onClick={() => handleNavigation('/blog')}
                className={`px-6 py-2 rounded-[20px] text-[0.9rem] font-medium transition-all duration-300 ${isActive('/blog') || location.pathname.startsWith('/blog/')
                  ? 'bg-[#27272a] text-white'
                  : 'text-[#9CA3AF] hover:text-white'
                  }`}
              >
                Blog
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4">
            {/* Open Studio Button - Always visible on Desktop */}
            <button
              onClick={onLaunchApp}
              className="hidden md:block px-5 py-2.5 rounded-lg bg-[#18181b] border border-[#27272a] text-white text-[0.9rem] font-medium hover:border-[#555] hover:bg-[#222] transition-all duration-300"
            >
              Open Studio
            </button>

            {user && (
              <Button
                variant="destructive"
                onClick={logout}
                className="bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
              >
                Sign Out
              </Button>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {mobileMenuOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" />
                ) : (
                  <path d="M3 12h18M3 6h18M3 18h18" />
                )}
              </svg>
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && appState === 'site' && (
        <div className="absolute top-full left-0 right-0 bg-[#050505] border-b border-white/10 p-4 md:hidden flex flex-col gap-4 shadow-xl">
          <button
            onClick={() => handleNavigation('/')}
            className="text-left text-gray-200 hover:text-teal-400 font-medium py-2"
          >
            Home
          </button>
          <button
            onClick={() => handleNavigation('/pricing')}
            className="text-left text-gray-200 hover:text-teal-400 font-medium py-2"
          >
            Pricing
          </button>
          <button
            onClick={() => handleNavigation('/guide')}
            className="text-left text-gray-200 hover:text-teal-400 font-medium py-2"
          >
            Guide
          </button>
          <button
            onClick={() => handleNavigation('/about')}
            className="text-left text-gray-200 hover:text-teal-400 font-medium py-2"
          >
            About
          </button>
          <button
            onClick={() => handleNavigation('/blog')}
            className="text-left text-gray-200 hover:text-teal-400 font-medium py-2"
          >
            Blog
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;